import { BaseApiClient } from '../base-api-client';
export function isBalanceRefreshThrottledApiError(err) {
    return (err instanceof Error &&
        err.status === 429 &&
        typeof err.retryAfterSeconds === 'number');
}
export class ExchangeClient extends BaseApiClient {
    constructor(config) {
        const fullConfig = {
            ...config,
            baseURL: `${config.baseURL}/exchanges`,
        };
        super(fullConfig, 'ExchangeClient');
    }
    async getAvailableExchanges() {
        try {
            const response = await this.get('/list/available');
            if (!response.success) {
                throw new Error(response.error || 'Failed to get available exchanges');
            }
            return response.data || [];
        }
        catch (error) {
            console.error('[ExchangeClient] Error getting available exchanges:', this.truncateError(error));
            throw error;
        }
    }
    async getUserExchanges(userId, includeBalances = false) {
        try {
            const url = `/${userId}${includeBalances ? '?includeBalances=true' : ''}`;
            const response = await this.get(url);
            console.log('[ExchangeClient] getUserExchanges response:', response);
            if (!response.success) {
                throw new Error(response.error || 'Failed to get user exchanges');
            }
            const data = response.data;
            if (!data) {
                return [];
            }
            if (Array.isArray(data)) {
                return data;
            }
            if (typeof data === 'object' && 'exchanges' in data && Array.isArray(data.exchanges)) {
                return data.exchanges;
            }
            console.warn('[ExchangeClient] Unexpected data format:', data);
            return [];
        }
        catch (error) {
            console.error('[ExchangeClient] Error getting user exchanges:', this.truncateError(error));
            return [];
        }
    }
    async setupExchange(userId, exchangeCode, apiKey, apiSecret, apiPassphrase) {
        try {
            const response = await this.post('/setup', {
                userId,
                exchangeCode,
                apiKey,
                apiSecret,
                passphrase: apiPassphrase,
            });
            if (!response.success) {
                throw new Error(response.error || 'Failed to setup exchange');
            }
        }
        catch (error) {
            console.error('[ExchangeClient] Error setting up exchange:', this.truncateError(error));
            throw error;
        }
    }
    async reorderExchanges(userId, exchangeCodes) {
        try {
            const response = await this.put(`/${userId}/priority`, {
                exchangeCodes,
            });
            if (!response.success) {
                throw new Error(response.error || 'Failed to update exchange priority');
            }
        }
        catch (error) {
            console.error('[ExchangeClient] Error reordering exchanges:', this.truncateError(error));
            throw error;
        }
    }
    async removeExchange(userId, exchangeCode) {
        try {
            const response = await this.delete(`/${userId}/${exchangeCode}`);
            if (!response.success) {
                throw new Error(response.error || 'Failed to remove exchange');
            }
        }
        catch (error) {
            console.error('[ExchangeClient] Error removing exchange:', this.truncateError(error));
            throw error;
        }
    }
    async refreshBalance(userId, exchangeCode) {
        try {
            const response = await this.post(`/${userId}/${exchangeCode}/refresh-balance`);
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to refresh balance');
            }
            return response.data;
        }
        catch (error) {
            const apiError = error;
            if (apiError.status === 429) {
                const body = apiError.response;
                const throttledError = new Error(body?.error || 'Balance refresh throttled');
                throttledError.status = 429;
                throttledError.isThrottled = true;
                throttledError.retryAfterSeconds = body?.retryAfterSeconds ?? 1800;
                throw throttledError;
            }
            console.error('[ExchangeClient] Error refreshing balance:', this.truncateError(error));
            throw error;
        }
    }
    async getUpbitAnnouncementKey(userId) {
        try {
            const response = await this.get(`/upbit-announcement/key/${userId}`);
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to get Upbit key');
            }
            return response.data;
        }
        catch (error) {
            console.error('[ExchangeClient] Error getting Upbit key:', this.truncateError(error));
            throw error;
        }
    }
    async setupUpbitAnnouncementKey(userId, apiKey, apiSecret) {
        try {
            const response = await this.post('/upbit-announcement/key', {
                userId,
                apiKey,
                apiSecret,
            });
            if (!response.success) {
                throw new Error(response.error || 'Failed to connect Upbit');
            }
        }
        catch (error) {
            console.error('[ExchangeClient] Error connecting Upbit:', this.truncateError(error));
            throw error;
        }
    }
    async removeUpbitAnnouncementKey(userId) {
        try {
            const response = await this.delete(`/upbit-announcement/key/${userId}`);
            if (!response.success) {
                throw new Error(response.error || 'Failed to remove Upbit');
            }
        }
        catch (error) {
            console.error('[ExchangeClient] Error removing Upbit:', this.truncateError(error));
            throw error;
        }
    }
}
//# sourceMappingURL=exchange-client.js.map